/**
 * Events tab (`ldt-log`, spec §11) — matches mockup `renderEventRows`:
 *   row = category-colour icon tile · (CATEGORY label / message) · HH:MM:SS.
 * A severity dot precedes the message only when severity !== 'ok'.
 * Category filter chips; reverse-chronological list scoped to this drone.
 */
import type { EventCategory, EventSeverity } from '@/lib/mqtt/schemas/events';
import { useAlertsStore } from '@/lib/store/alerts';
import type { DroneLive } from '@/lib/store/drones';
import { foreignTenant } from '@/lib/tenancy';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useShallow } from 'zustand/shallow';

const CATEGORIES: { key: EventCategory | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'System', label: 'System' },
  { key: 'Mission', label: 'Mission' },
  { key: 'Power', label: 'Power' },
  { key: 'Comms', label: 'Comms' },
  { key: 'DAA', label: 'DAA' },
];

// Per-category icon + colour + tinted background (mockup `EV_CAT`).
const EV_CAT: Record<string, { icon: string; color: string; bg: string }> = {
  System: { icon: '⚙', color: 'var(--color-blue)', bg: 'rgba(37,99,235,.10)' },
  Mission: { icon: '⚑', color: 'var(--color-green)', bg: 'rgba(29,122,64,.10)' },
  Power: { icon: '⚡', color: 'var(--color-amber)', bg: 'rgba(180,83,9,.10)' },
  Comms: { icon: '⌹', color: 'var(--color-blue)', bg: 'rgba(37,99,235,.10)' },
  DAA: { icon: '✈', color: 'var(--color-red)', bg: 'rgba(196,26,26,.10)' },
  OI: { icon: '◈', color: 'var(--color-amber)', bg: 'rgba(180,83,9,.10)' },
  Surveillance: { icon: '❖', color: 'var(--color-blue)', bg: 'rgba(37,99,235,.10)' },
  Infrastructure: { icon: '⌂', color: 'var(--color-text2)', bg: 'var(--color-bg)' },
};
const DEFAULT_CAT = { icon: '•', color: 'var(--color-text3)', bg: 'var(--color-bg)' };

const EV_SEV: Record<EventSeverity, string> = {
  ok: 'var(--color-green)',
  warn: 'var(--color-amber)',
  crit: 'var(--color-red)',
  info: 'var(--color-blue)',
};

function hhmmss(iso: string): string {
  return new Date(iso).toTimeString().slice(0, 8);
}

export function EventsTab({ drone }: { drone: DroneLive }) {
  const [cat, setCat] = useState<EventCategory | 'all'>('all');
  const events = useAlertsStore(useShallow((s) => s.items.filter((e) => e.drone_id === drone.id)));
  const visible = cat === 'all' ? events : events.filter((e) => e.category === cat);

  return (
    <div className="flex flex-col">
      {/* Filter chips (mockup .ev-chips) */}
      <div className="flex gap-1.5 flex-wrap mb-2.5">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setCat(c.key)}
            className={cn(
              'h-6 px-2.5 rounded-full border text-[0.769em] font-semibold transition-colors',
              c.key === cat
                ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
                : 'bg-(--color-bg) border-(--color-border) text-(--color-text3) hover:border-(--color-green) hover:text-(--color-green)',
            )}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Event rows (mockup .ev-row) */}
      <div>
        {visible.length === 0 && (
          <div className="text-[0.846em] text-(--color-text3) font-mono text-center py-4">
            No {cat === 'all' ? '' : `${cat} `}events
          </div>
        )}
        {visible.map((e) => {
          const c = EV_CAT[e.category] ?? DEFAULT_CAT;
          return (
            <div
              key={e.event_id}
              className="flex items-start gap-2 py-[7px] border-b border-(--color-border-lite) last:border-b-0"
            >
              <div
                className="w-[22px] h-[22px] rounded-[5px] flex items-center justify-center text-[0.846em] shrink-0 mt-px"
                style={{ background: c.bg, color: c.color }}
              >
                {c.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-[0.615em] font-bold uppercase tracking-[0.5px] mb-px"
                  style={{ color: c.color }}
                >
                  {e.category}
                </div>
                <div className="text-[0.846em] text-(--color-text) leading-[1.3]">
                  {/* `sourceTenant` on the event marks a partner tenant's
                      event arriving on our own `v1/output/events` topic —
                      same rule as the drone markers. Own-tenant events
                      render unchanged. */}
                  {foreignTenant(e.source_tenant) && (
                    <span className="inline-block mr-1 px-1 py-px rounded bg-(--color-bg) border border-(--color-border) text-(--color-text3) text-[0.769em] font-bold align-middle">
                      {foreignTenant(e.source_tenant)}
                    </span>
                  )}
                  {e.severity !== 'ok' && (
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full mr-[3px] align-middle mb-px"
                      style={{ background: EV_SEV[e.severity] }}
                    />
                  )}
                  {e.message}
                </div>
              </div>
              <div className="text-[0.692em] font-mono text-(--color-text3) shrink-0 mt-[3px]">
                {hhmmss(e.timestamp)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
